import {RoleBasedPermission} from '@hconnect/apiclient'
import {getCurrentUserId} from '@hconnect/common/authentication/getCurrentUserId'
import {useUpmPlantsContext} from '@hconnect/upm'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {generatePath, useNavigate} from 'react-router'

import {ArchiveConfirmModal, EditSiteModal} from '../components'
import {buildImportPlantOptions, type ImportPlantOption} from '../helpers'
import {
  useAllReports,
  useArchiveReport,
  useExportReport,
  useReports,
  useRiskSurveyPlantsByPermission,
  useRiskSurveyQueryState,
  useRiskSurveyTranslation,
  useUnarchiveReport,
  useUpdateReportSite
} from '../hooks'
import {RiskSurveyOverviewPage} from '../pages'
import {RISK_SURVEY_PLANT} from '../routes/routes'
import type {ReportSummaryDto, UpdateReportSiteRequest} from '../types'

import {ImportReportContainer} from './ImportReportContainer'
import {calculateOverviewKpi} from './riskSurveyOverview.helpers'

export const RiskSurveyOverviewContainer = () => {
  const {state, setFilters, setPagination, setSorting} = useRiskSurveyQueryState()
  const navigate = useNavigate()
  const {t} = useRiskSurveyTranslation()
  const currentUserId = useMemo(() => getCurrentUserId(), [])
  const upmPlants = useUpmPlantsContext()

  const {page, pageSize, plant, includeArchived, sortBy, sortDir} = state
  const plantFilter = plant ? [plant] : []

  const [importOpen, setImportOpen] = useState(false)
  const [archiveTargetId, setArchiveTargetId] = useState<string | null>(null)
  const [editTargetReport, setEditTargetReport] = useState<ReportSummaryDto | null>(null)

  const {plantIds: manageableUpmIds} = useRiskSurveyPlantsByPermission(
    RoleBasedPermission.HproduceRoleQualityManager
  )
  const {plantIds: viewableUpmIds} = useRiskSurveyPlantsByPermission(
    RoleBasedPermission.HproduceRoleViewer
  )
  const canManage = manageableUpmIds.length > 0

  const editableUpmIds = useMemo(
    () => Array.from(new Set([...manageableUpmIds, ...viewableUpmIds])),
    [manageableUpmIds, viewableUpmIds]
  )

  const baseEditPlantOptions: ImportPlantOption[] = useMemo(
    () =>
      buildImportPlantOptions(editableUpmIds, upmPlants, (id) =>
        t('plant.fallbackName', {upmId: id})
      ),
    [editableUpmIds, upmPlants, t]
  )

  const editPlantOptions: ImportPlantOption[] = useMemo(() => {
    if (!editTargetReport) {
      return baseEditPlantOptions
    }

    const hasCurrentReportPlant = baseEditPlantOptions.some(
      (option) => option.upmId === editTargetReport.upmId
    )
    if (hasCurrentReportPlant) {
      return baseEditPlantOptions
    }

    return [
      ...baseEditPlantOptions,
      {
        upmId: editTargetReport.upmId,
        plantId: editTargetReport.plantId,
        siteName:
          editTargetReport.siteName || t('plant.fallbackName', {upmId: editTargetReport.upmId})
      }
    ].sort((left, right) => left.siteName.localeCompare(right.siteName))
  }, [baseEditPlantOptions, editTargetReport, t])

  const {data, isLoading, isError} = useReports({
    upmId: plantFilter[0],
    page,
    pageSize,
    includeArchived,
    sortBy,
    sortDir
  })
  const {data: filteredReports} = useAllReports({
    upmId: plantFilter[0],
    includeArchived
  })
  const {data: allOverviewReports} = useAllReports({includeArchived})

  const reportsForSummary = useMemo(
    () => filteredReports ?? data?.items ?? [],
    [filteredReports, data?.items]
  )
  const reportsForPlantOptions = allOverviewReports ?? reportsForSummary

  const showCountry = useMemo(() => {
    if (!reportsForSummary.length) {
      return false
    }

    return (
      new Set(
        reportsForSummary
          .map((item) => item.country)
          .filter((country): country is string => !!country)
      ).size > 1
    )
  }, [reportsForSummary])

  useEffect(() => {
    if (!showCountry || sortBy || sortDir) {
      return
    }

    setSorting('country', 'asc')
  }, [setSorting, showCountry, sortBy, sortDir])

  const {mutate: archiveReport, isPending: isArchiving} = useArchiveReport({
    onSettled: () => setArchiveTargetId(null)
  })
  const {mutate: unarchiveReport} = useUnarchiveReport()
  const {mutate: updateReportSite, isPending: isUpdatingSite} = useUpdateReportSite({
    onSuccess: () => setEditTargetReport(null)
  })
  const {mutate: exportReport} = useExportReport()

  const kpi = useMemo(() => calculateOverviewKpi(reportsForSummary), [reportsForSummary])

  const plantOptions = useMemo(() => {
    if (!reportsForPlantOptions.length) return []
    const seen = new Map<string, {plantId: string; siteName: string}>()
    reportsForPlantOptions.forEach((r) => {
      if (!seen.has(r.upmId)) {
        seen.set(r.upmId, {plantId: r.plantId, siteName: r.siteName})
      }
    })
    return Array.from(seen, ([upmId, {plantId, siteName}]) => ({
      value: upmId,
      label: `${siteName} (${plantId})`
    }))
  }, [reportsForPlantOptions])

  const handlePlantFilterChange = useCallback(
    (values: string[]) => setFilters({plant: values[0] ?? ''}),
    [setFilters]
  )

  const handleIncludeArchivedChange = useCallback(
    (value: boolean) => setFilters({includeArchived: value}),
    [setFilters]
  )

  const handleArchive = useCallback((reportId: string) => setArchiveTargetId(reportId), [])

  const handleConfirmArchive = useCallback(() => {
    if (archiveTargetId) archiveReport({reportId: archiveTargetId})
  }, [archiveTargetId, archiveReport])

  const handleUnarchive = useCallback(
    (reportId: string) => {
      unarchiveReport({reportId})
    },
    [unarchiveReport]
  )

  const handleEditSite = useCallback((report: ReportSummaryDto) => setEditTargetReport(report), [])

  const handleConfirmEditSite = useCallback(
    (data: UpdateReportSiteRequest) => {
      if (editTargetReport) {
        updateReportSite({reportId: editTargetReport.id, data})
      }
    },
    [editTargetReport, updateReportSite]
  )

  const handleExport = useCallback(
    (reportId: string, siteName: string) => {
      exportReport({reportId, fileName: `risk-survey-${siteName || 'report'}.xlsx`})
    },
    [exportReport]
  )

  const handleReportClick = useCallback(
    (report: ReportSummaryDto) => {
      const plantPath = generatePath(RISK_SURVEY_PLANT, {upmId: report.upmId})
      void navigate(`${plantPath}?reportId=${report.id}`)
    },
    [navigate]
  )

  return (
    <>
      <RiskSurveyOverviewPage
        data={data}
        kpi={kpi}
        isLoading={isLoading}
        isError={isError}
        onPageChange={setPagination}
        onImport={() => setImportOpen(true)}
        canManage={canManage}
        filterProps={{
          plantFilter,
          onPlantFilterChange: handlePlantFilterChange,
          plantOptions,
          includeArchived,
          onIncludeArchivedChange: handleIncludeArchivedChange
        }}
        tableProps={{
          showCountry,
          onSortChange: setSorting,
          currentUserId,
          onEditSite: handleEditSite,
          onArchive: handleArchive,
          onUnarchive: handleUnarchive,
          onExport: handleExport,
          onReportClick: handleReportClick
        }}
      />
      <ArchiveConfirmModal
        open={archiveTargetId !== null}
        onClose={() => setArchiveTargetId(null)}
        onConfirm={handleConfirmArchive}
        isLoading={isArchiving}
      />
      <EditSiteModal
        open={editTargetReport !== null}
        report={editTargetReport}
        plantOptions={editPlantOptions}
        isLoading={isUpdatingSite}
        onClose={() => setEditTargetReport(null)}
        onConfirm={handleConfirmEditSite}
      />
      {importOpen && (
        <ImportReportContainer open={importOpen} onClose={() => setImportOpen(false)} />
      )}
    </>
  )
}
